/**
 * Pure helpers for the Commission Breakdown page (/commission-breakdown,
 * MonthlyTableDashboardPage.tsx). Kept out of the component so the period,
 * month-spine and request rules are testable without rendering the page.
 *
 * Year semantics (unchanged request contract): in financial mode the selected
 * year is the START year of the financial year, so 2023 means Jul 2023 to
 * Jun 2024, labelled "FY2024" (Australian financial years are named by the
 * year they end in). In calendar mode it is the calendar year.
 */

import { businessMonth } from './businessTime';
import { firstLoadedMonth, lastLoadedMonth, mayZeroFill } from './monthlyZeroFill';

export type PeriodMode = 'calendar' | 'financial' | 'allTime';

/** Fixed English abbreviations: month labels must not depend on the runtime ICU build. */
export const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export function monthLabel(month: number): string {
  return MONTH_ABBREVIATIONS[month - 1] ?? '';
}

const COUNT_FORMAT = new Intl.NumberFormat('en-AU');

/** Count with en-AU grouping, independent of the browser locale. */
export function formatCount(value: number): string {
  return COUNT_FORMAT.format(Number.isFinite(value) ? value : 0);
}

/** Calendar year of `now` in the business timezone (Australia/Brisbane). */
export function currentCalendarYear(now: Date = new Date()): number {
  return parseInt(businessMonth(now).slice(0, 4), 10);
}

/** Start year of the financial year (Jul to Jun) `now` falls in, Brisbane time. */
export function currentFinancialYearStart(now: Date = new Date()): number {
  const [year, month] = businessMonth(now).split('-').map((p) => parseInt(p, 10));
  return month >= 7 ? year : year - 1;
}

/** The year the page should select when it opens in, or switches to, `mode`. */
export function defaultYearFor(mode: PeriodMode, now: Date = new Date()): number {
  return mode === 'financial' ? currentFinancialYearStart(now) : currentCalendarYear(now);
}

/** "FY2024 (Jul 2023 to Jun 2024)" for start year 2023. */
export function financialYearLabel(startYear: number): string {
  return `FY${startYear + 1} (Jul ${startYear} to Jun ${startYear + 1})`;
}

export interface YearOption {
  value: number;
  label: string;
}

/**
 * Year dropdown options, newest first.
 *
 * Financial mode runs from the current financial year back to the one that
 * CONTAINS the first data month: data starting in Mar 2023 belongs to the
 * financial year that started Jul 2022, so `earliestYear - 1` is included
 * (without it that first financial year could not be selected at all).
 */
export function buildYearOptions(
  mode: Exclude<PeriodMode, 'allTime'>,
  earliestYear: number | undefined,
  latestYear: number | undefined,
  now: Date = new Date(),
): YearOption[] {
  const calendarNow = currentCalendarYear(now);
  const earliest = earliestYear ?? calendarNow - 4;
  const options: YearOption[] = [];
  if (mode === 'financial') {
    for (let y = currentFinancialYearStart(now); y >= earliest - 1; y--) {
      options.push({ value: y, label: financialYearLabel(y) });
    }
    return options;
  }
  for (let y = Math.max(latestYear ?? calendarNow, calendarNow); y >= earliest; y--) {
    options.push({ value: y, label: String(y) });
  }
  return options;
}

/** The 'YYYY-MM' months of a calendar or financial year, in order. */
export function monthsOfYear(year: number, mode: Exclude<PeriodMode, 'allTime'>): string[] {
  return Array.from({ length: 12 }, (_, i) => {
    const monthIndex = mode === 'financial' ? (i + 6) % 12 : i;
    const y = mode === 'financial' && monthIndex < 6 ? year + 1 : year;
    return `${y}-${String(monthIndex + 1).padStart(2, '0')}`;
  });
}

/**
 * Months to add as zero rows for a year window.
 *
 * - No data in the window at all: nothing. The page shows its empty state
 *   instead of twelve fabricated $0 months.
 * - Otherwise: the gaps between the first and last month carrying data
 *   (utils/monthlyZeroFill.ts), never after the current Brisbane month.
 */
export function zeroFillMonths(
  existingMonths: ReadonlyArray<string>,
  year: number,
  mode: Exclude<PeriodMode, 'allTime'>,
  now: Date = new Date(),
): string[] {
  if (existingMonths.length === 0) return [];
  const rows = existingMonths.map((yearMonth) => ({ yearMonth }));
  const latest = lastLoadedMonth(rows);
  const earliest = firstLoadedMonth(rows);
  const current = businessMonth(now);
  const present = new Set(existingMonths);
  return monthsOfYear(year, mode).filter(
    (ym) => !present.has(ym) && ym <= current && mayZeroFill(ym, latest, earliest),
  );
}

export interface MonthlyTableRequestInput {
  companyId: string;
  periodMode: PeriodMode;
  selectedYear: number;
  entityCode?: string;
  templateId?: string;
  includeDeductionDetail: boolean;
}

/**
 * The getMonthlyTableData payload. Optional members are only present when
 * set, so the payload (and the query cache key built from it) is identical to
 * what the page sent before for every unfiltered request.
 */
export function buildMonthlyTableRequest(input: MonthlyTableRequestInput): {
  companyId: string;
} & Record<string, unknown> {
  const params: { companyId: string } & Record<string, unknown> = { companyId: input.companyId };
  if (input.periodMode === 'allTime') {
    params.allTime = true;
  } else {
    params.year = input.selectedYear;
    params.yearType = input.periodMode;
  }
  if (input.entityCode) {
    params.entityCode = input.entityCode;
  } else if (input.templateId) {
    // A loan book that resolves to a template only (no entity code) narrows by
    // template; without this the Loan Book filter silently showed every book.
    params.templateId = input.templateId;
  }
  if (input.includeDeductionDetail) params.includeDeductionDetail = true;
  return params;
}
